import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type AppMode = 'demo' | 'live';

interface ModeContextType {
  mode: AppMode;
  setMode: (mode: AppMode) => void;
  isDemo: boolean;
  isLive: boolean;
  toggleMode: () => void;
}

const ModeContext = createContext<ModeContextType | undefined>(undefined);

const MODE_STORAGE_KEY = 'app-mode';

interface ModeProviderProps {
  children: ReactNode;
  defaultMode?: AppMode;
}

export function ModeProvider({ children, defaultMode = 'demo' }: ModeProviderProps) {
  // Initialize mode from localStorage or use default
  const [mode, setModeState] = useState<AppMode>(() => {
    try {
      const stored = localStorage.getItem(MODE_STORAGE_KEY);
      return (stored === 'demo' || stored === 'live') ? stored : defaultMode;
    } catch (error) {
      console.error('Failed to read mode from localStorage:', error);
      return defaultMode;
    }
  });

  // Persist mode changes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(MODE_STORAGE_KEY, mode);
    } catch (error) {
      console.error('Failed to save mode to localStorage:', error);
    }
  }, [mode]);

  const setMode = (newMode: AppMode) => {
    setModeState(newMode);
  };

  const toggleMode = () => {
    setModeState((prevMode) => (prevMode === 'demo' ? 'live' : 'demo'));
  };

  const value: ModeContextType = {
    mode,
    setMode,
    isDemo: mode === 'demo',
    isLive: mode === 'live',
    toggleMode,
  };

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

export function useMode() {
  const context = useContext(ModeContext);
  if (context === undefined) {
    throw new Error('useMode must be used within a ModeProvider');
  }
  return context;
}
